import { FC, memo } from 'react';

import { TEXTS } from 'application/pages/texts';
import { TEXTS as POINT_TEXTS } from 'application/pages/PointsPage/renderer/texts';
import { UseDebouncedSuggestionAddressType } from 'application/hooks/useAddressState';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useAccountCompanyCountry } from 'application/hooks';

import { Country } from 'domain/api/graphql/generated';

import Autocomplete from '@zvuk-b2b/react-uikit/ui/Autocomplete';
import { DropdownPlacement } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';

import styles from './index.module.scss';

interface ModalContentProps {
  isAcceptanceAdvancedNull: boolean;
  isAcceptanceAdvancedPending: boolean;
  addressState: UseDebouncedSuggestionAddressType;
  createPointError?: Error;
  isShowHintAboutPrice?: boolean;
  openModal: ((type: ModalTypes, props: {}) => void) &
    (<T extends ModalTypes>(
      type: T,
      modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
      onModalCloseCallback?: () => void
    ) => void);
}

const getPlaceholder = (companyCountry?: Country) => {
  switch (companyCountry) {
    case Country.By:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_BY;
    case Country.Kz:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_KZ;
    default:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_RU;
  }
};

const ModalContent: FC<ModalContentProps> = ({
  isAcceptanceAdvancedNull,
  isAcceptanceAdvancedPending,
  addressState,
  createPointError,
  isShowHintAboutPrice,
  openModal,
}) => {
  const companyCountry = useAccountCompanyCountry();

  const onPointCreateHintModalShow = () => {
    openModal(ModalTypes.POINT_CREATE_HINT, {});
  };

  if (isAcceptanceAdvancedNull) {
    return <div className={styles.contentSkeleton} />;
  }

  return (
    <>
      <Autocomplete
        elementId="input_create_point_modal_address"
        autoComplete="off"
        placeholder={getPlaceholder(companyCountry)}
        value={addressState.value}
        options={addressState.options || []}
        error={createPointError?.message}
        isLoading={addressState.loading}
        onChange={addressState.onChange}
        dropdownPlacement={DropdownPlacement.BOTTOM}
      />

      <Button
        elementId="button_show_create_point_with_payment_hint_modal"
        type={ButtonType.PLAIN}
        className={styles.hintButton}
        onClick={onPointCreateHintModalShow}>
        {TEXTS.POINT_CREATE_HINT_MODAL_TITLE}
      </Button>

      {isShowHintAboutPrice && !isAcceptanceAdvancedPending && (
        <Info className={styles.hintInfo}>{POINT_TEXTS.POINT_CREATE_HINT_COST_MODAL_TITLE}</Info>
      )}

      {isAcceptanceAdvancedPending && (
        <Info
          type={InfoTypes.WARNING}
          className={styles.hintInfo}>
          <div>{POINT_TEXTS.POINT_CREATE_HINT_ACCEPTANCE_ADVANCED_1}</div>
          <div>{POINT_TEXTS.POINT_CREATE_HINT_ACCEPTANCE_ADVANCED_2}</div>
        </Info>
      )}
    </>
  );
};

export default memo(ModalContent);
